import React from 'react';
import { ArrowRight, ExternalLink, ChevronRight } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function OnConflict() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const en = {
    back: 'Writing',
    badge: 'Five articles',
    title: 'On Conflict',
    intro: 'Most organisations treat conflict as a problem to be managed. Something to de-escalate, smooth over, or resolve as quickly as possible so that everyone can get back to the more comfortable business of getting along. This series argues that this instinct - understandable as it is - is one of the more expensive mistakes that leaders and organisations make. Not because conflict is pleasant, or because the destructive end of it is not genuinely damaging. But because the goal of minimising conflict tends to produce something worse than conflict: silence. And silence, in most organisations, is not the absence of conflict. It is conflict that has gone underground. The five articles cover what conflict actually is, what happens to you when you are in one, how to diagnose what you are dealing with, what a conflict coach actually does, and how much of the conflict in organisations is not caused by the people in it but by the conditions around them. The central argument is simple enough to state in a sentence: most of what we call conflict problems are clarity problems. The conflict was there before it became visible. The question is what to do about it before it reaches the point where everyone can see it - and what to do when it does.',
    articles: [{
      n: '01',
      title: 'Conflict is not the problem',
      desc: 'The goal of minimising conflict tends to produce something worse than conflict - silence. This article reframes what healthy conflict looks like and why the most common instinct about it is wrong.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '02',
      title: 'What happens to you under pressure',
      desc: 'The gap between who you intend to be in a conflict and who you actually are is not a character failing. It is a physiological fact. And understanding it is the foundation of everything else.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '03',
      title: 'What is actually going on',
      desc: 'Most conflict interventions fail because they address the presenting problem rather than the underlying one. This article is about diagnosing what you are actually dealing with before you decide what to do.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '04',
      title: 'What a conflict coach actually does',
      desc: 'Most people who try to help with a conflict end up making it worse - not because they do not care, but because they are confused about what role they are actually in. This article is about that confusion and what to do instead.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '05',
      title: 'The conflicts we create without knowing it',
      desc: 'W. Edwards Deming estimated that eighty-five percent of problems in any organisation are system problems rather than people problems. Most conflict is no different. This article is about the eighty-five percent.',
      href: 'https://helgig.substack.com',
      live: false
    }],
    substackCta: 'Follow on Substack - series publishing soon',
    relatedTitle: 'Related',
    related: [{
      label: 'Conflict Coaching course',
      href: '/services/courses/conflict-coaching'
    }, {
      label: 'First-Time Manager',
      href: '/services/courses/first-time-manager'
    }]
  };
  const is = {
    back: 'Skrif',
    badge: 'Fimm greinar',
    title: 'Um Ágreininga',
    intro: `Flestir vinnustaðir líta á ágreininga sem vandamál sem þarf að stjórna. Eitthvað sem þarf að draga úr, slétta yfir eða leysa eins fljótt og auðið er svo allir geti snúið aftur að því sem er miklu jákvæðara, að öllum komi vel saman.

Þessi greinaröð skoðar hvað veldur því að þessi hvati - eins mannlegur og skiljanlegur eins og hann er - verði svo oft með dýrari gryfjum sem leiðtogar og stofnanir falla í.

Ekki vegna þess að átök séu þægileg eða ánægjuleg, né vegna þess að skemmandi hliðar þeirra sé ekki í raun skaðlegar. Fremur vegna þess að sú hugsun og hegðun að lágmarka átök leiðir yfirleitt til þess sem í praxís er verra en átök: þögnin. Þögnin á flestum vinnustöðum er ekki fjarvera ágreininga - hún þýðir að ágreiningar hafa farið undir yfirborðið.

Þessi greinaröð er fimm greinar sem fjalla um hvað átök í raun eru, hvað gerist þegar við erum stödd í þeim, hvernig greina má hvað sé raunverulega í gangi, hvað Conflict Coach nálgun gerir í raun og hversu stór hluti ágreininga á vinnustöðum fjallar í raun ekki um fólkið sjálft heldur snýr meira að aðstæðum og umgjörðinni sem við störfum í.

Meginþemu má færa saman í eina kjarna innsýn: flest það sem við köllum ágreinings vandamál snýst fremur um skýrleika, eða öllu heldur skortinn á. Ágreiningar voru til staðar áður en þeir urðu sýnilegir. Spurningin er hvað eigum við að gera áður en þeir verða sýnilegir - og þegar þeir blossa upp á yfirborðið, hvað við getum þá gert í málunum.`,
    articles: [{
      n: '01',
      title: 'Ágreiningur er ekki vandinn',
      desc: 'Markmið að lágmarka ágreining leiðir yfirleitt til þess sem er verra en ágreiningur - þögnin. Þessi grein endurræmir hvernig heilbrigður ágreiningur lítur út og hvers vegna algengasta hvatin um hann er röng.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '02',
      title: 'Hvað gerist með þig þegar þrýstingurinn eykst',
      desc: 'Bilurinn milli þess sem þú hyggst vera í ágreiningi og þess sem þú raunverulega ert er ekki persónulegur bristi. Það er líffærafræðileg staðreynd. Og að skilja hana er grunnurinn að öllu öðru.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '03',
      title: 'Hvað er raunverulega í gangi',
      desc: 'Flestar inngrip í ágreining mistakast vegna þess að þær taka á kynntu vandanum frekar en undirliggjandi. Þessi grein fjallar um að greina hvað þú ert raunverulega að eiga við áður en þú ákveður hvað á að gera.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '04',
      title: 'Hvað Conflict Coach gerir raunverulega',
      desc: 'Flestir sem reyna að hjálpa með ágreining enda með að gera hann verri - ekki vegna þess að þeir hugsi ekki um viðkomandi, heldur vegna þess að þeir rugla sig á hvaða hlutverki þeir eru í raun. Þessi grein fjallar um þann rugling og hvað á að gera í staðinn.',
      href: 'https://helgig.substack.com',
      live: false
    }, {
      n: '05',
      title: 'Ágreiningar sem við sköpum án þess að vita af',
      desc: 'W. Edwards Deming mat að 85% vandamála í hverri stofnun séu kerfisleg vandamál frekar en mannleg. Flestir ágreiningar eru ekki öðruvísi. Þessi grein fjallar um þessi 85%.',
      href: 'https://helgig.substack.com',
      live: false
    }],
    substackCta: 'Fylgjast á Substack - greinaröðin kemur fljótlega',
    relatedTitle: 'Tengt',
    related: [{
      label: 'Conflict Coaching námskeið',
      href: '/services/courses/conflict-coaching'
    }, {
      label: 'Stjórnandi í fyrsta sinn',
      href: '/services/courses/first-time-manager'
    }]
  };
  const c = isIS ? is : en;
  return <PageTransition data-id="element-2200" data-mp="4gtqi1p0lhwd">
      <div className="pt-12 pb-24" data-id="element-2201" data-mp="pirovqbb9a1u">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2202" data-mp="pjt4m839ukpw">
          <Animate data-id="element-2203" data-mp="kkghrncehdnb">
            <Link to="/writing" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal mb-8 transition-colors" data-id="element-2204" data-mp="kkznm5os0bkz">
              <ChevronRight size={14} className="rotate-180" data-id="element-2205" data-mp="uhm7ar5d8wlf" />
              {c.back}
            </Link>
          </Animate>
          <Animate data-id="element-2206" data-mp="188hhq7vijlr4">
            <span className="inline-block px-3 py-1 bg-teal/10 text-teal font-medium rounded-full text-xs mb-4" data-id="element-2207" data-mp="187pov4taw24j">
              {c.badge}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-6" data-id="element-2208" data-mp="1i88dsdzq28tb">
              {c.title}
            </h1>
            <div className="prose prose-lg text-gray-500 leading-relaxed mb-12 space-y-4" data-id="element-2209" data-mp="1d9ncsccnbl3t">
              {c.intro.split('\n\n').map((para, i) => <p key={i} data-id="element-2210" data-mp="3ezyujykbpg1s" data-mpi={i}>{para}</p>)}
            </div>
          </Animate>
          <div className="space-y-4 mb-12" data-id="element-2211" data-mp="3f2bm93fvv4s1">
            {c.articles.map((a, i) => <Animate key={a.n} delay={i * 0.05} data-id="element-2212" data-mp="1uipxd5hdtwwl" data-mpi={i}>
                <Card className="p-6 border border-gray-100 opacity-70" data-id="element-2213" data-mp="1zhapsiqe7cah">
                  <div className="flex items-start gap-4" data-id="element-2214" data-mp="1ks4rqy3ajs46">
                    <span className="text-2xl font-bold text-gray-200 font-mono flex-shrink-0 leading-none mt-1" data-id="element-2215" data-mp="1krljya7cuk3v">
                      {a.n}
                    </span>
                    <div className="flex-1" data-id="element-2216" data-mp="1br6vx330aqwo">
                      <div className="flex items-center gap-2 mb-1" data-id="element-2217" data-mp="1btjffkoex9bu">
                        <h2 className="font-bold text-charcoal" data-id="element-2218" data-mp="1grb3g3lxsbar">{a.title}</h2>
                        <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full font-medium" data-id="element-2219" data-mp="11u2bb7ot73hz">
                          Kemur fljótlega
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2220" data-mp="2w8cw952a9dih">
                        {a.desc}
                      </p>
                    </div>
                  </div>
                </Card>
              </Animate>)}
          </div>
          <Animate data-id="element-2221" data-mp="m56zelayu5uk">
            <Card className="p-6 bg-teal/5 border-teal/20 border text-center mb-12" data-id="element-2222" data-mp="m5p0pwchja9c">
              <p className="text-gray-600 mb-4" data-id="element-2223" data-mp="7gj84r85kiaf">{c.substackCta}</p>
              <a href="https://helgig.substack.com" target="_blank" rel="noopener noreferrer" data-id="element-2224" data-mp="7e6h7okf2xl8">
                <Button variant="outline" rightIcon={<ExternalLink size={16} data-id="element-2226" data-mp="153rmm29qkr2x" />} data-id="element-2225" data-mp="105zxth4e1l2y">
                  Substack
                </Button>
              </a>
            </Card>
          </Animate>
          <Animate data-id="element-2227" data-mp="q8v6l2voqzfa">
            <div data-id="element-2228" data-mp="v5vgxpzkioc7">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2229" data-mp="v5c42noryq51">
                {c.relatedTitle}
              </h3>
              <div className="flex flex-wrap gap-3" data-id="element-2230" data-mp="3avopty80k8mp">
                {c.related.map(r => <Link key={r.href} to={r.href} data-id="element-2231" data-mp="3fxe21h6z2scd">
                    <Button variant="outline" size="sm" rightIcon={<ArrowRight size={14} data-id="element-2233" data-mp="1sybhp2noh1mo" />} data-id="element-2232" data-mp="1xxy4o3tb5w8o">
                      {r.label}
                    </Button>
                  </Link>)}
              </div>
            </div>
          </Animate>
        </div>
      </div>
    </PageTransition>;
}